/**
 * What waits for the person's answer, counted one way everywhere (the Inbox, the rail badge, the
 * phone's Inbox tab, Overview's tile): a view over the one `needsYou` selector (ADR 0056
 * decision 6). Blocked: every pending approval, every request that stopped its session (expired
 * or denied, not handled, R-HL-3), every assistant step waiting for approval, and every waiting
 * session no request covers whose turn did not end on a question. Asked: sessions whose turn
 * ended on a question, for 24 hours, unless dismissed or snoozed. A session held at a request
 * counts once, as the request.
 */
import { useMemo } from 'react';
import { approvalOutcomeOf, needsYou } from '@orgus/protocol';
import type { ApprovalAnswerer, ApprovalRequest, ApprovalsState, AskSetAside, NeedsYou, NeedsYouStep, Session } from '@orgus/protocol';
import { sortPending } from './live.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';

export interface WaitingOnYou {
  approvals: ApprovalRequest[];
  /** Waiting sessions no request covers that are blocked (Needs you), oldest first. */
  sessions: Session[];
  /** Sessions whose turn ended on a question (Asked), oldest first. */
  asked: Session[];
  /** Decided requests that still block their session (the Inbox's Blocked section). */
  blocked: number;
  /** Assistant steps waiting for approval (the Inbox's assistant section). */
  assistant: number;
  /** approvals + sessions + asked: what the Inbox's "Waiting for you" heading counts. */
  asking: number;
  /** Everything that is blocked (amber): approvals, stopped requests, assistant steps, blocked sessions. */
  urgent: number;
  /** asking + blocked + assistant: the rail badge, the phone tab and Overview's tile (needs.count). */
  total: number;
  needs: NeedsYou;
}

/** Decided requests that still block a session: expired with the session parked, or denied; not handled. */
export function blockedRequests(blocking: readonly ApprovalRequest[] | null | undefined): number {
  return (blocking ?? []).filter((a) => approvalOutcomeOf(a) && !a.dismissedAt && !a.laterWorkAt).length;
}

/** The Inbox's reading of the selector. */
export function waitingFrom(needs: NeedsYou): WaitingOnYou {
  const approvals = sortPending(needs.blocked.filter((i) => i.kind === 'request').map((i) => i.approval!));
  const sessions = needs.blocked.filter((i) => i.kind === 'session').map((i) => i.session!);
  const asked = needs.asked.map((i) => i.session!);
  const blocked = needs.blocked.filter((i) => i.kind === 'stopped').length;
  const assistant = needs.blocked.filter((i) => i.kind === 'step').length;
  const asking = approvals.length + sessions.length + asked.length;
  return { approvals, sessions, asked, blocked, assistant, asking, urgent: needs.blocked.length, total: needs.count, needs };
}

/** waitingFrom over raw lists (tests, and the phone's own copy). `assistant` is a count or the steps. */
export function waitingOnYou(
  pending: readonly ApprovalRequest[],
  sessions: Iterable<Session>,
  blocking: readonly ApprovalRequest[] = [],
  assistant: number | readonly NeedsYouStep[] = 0,
  setAside: readonly AskSetAside[] = [],
  now = Date.now(),
  answerer?: ApprovalAnswerer,
): WaitingOnYou {
  const steps = typeof assistant === 'number' ? Array.from({ length: assistant }, (_, i) => ({ id: `step-${i}`, title: '', at: now })) : assistant;
  return waitingFrom(needsYou({ sessions, pending, blocking, steps, setAside, ...(answerer ? { answerer } : {}), now }));
}

/** waitingOnYou over an approvals state. */
export function waitingOnYouOf(state: Partial<ApprovalsState> | null | undefined, sessions: Iterable<Session>, assistant = 0, setAside: readonly AskSetAside[] = []): WaitingOnYou {
  return waitingOnYou(state?.pending ?? [], sessions, state?.blocking ?? [], assistant, setAside, Date.now(), state?.answerer?.answerer);
}

export function useWaitingOnYou(): WaitingOnYou {
  const needs = useNeedsYouState();
  return useMemo(() => waitingFrom(needs), [needs]);
}

/** The one number: the Inbox, the rail badge, the phone tab and Overview agree on it. */
export function useWaitingCount(): number {
  return useNeedsYouState().count;
}
